import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';

const PersonalData = () => {
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(true);
  const [savingNames, setSavingNames] = useState(false);
  const [savingPii, setSavingPii] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [namesLocked, setNamesLocked] = useState(false);
  const [cpf, setCpf] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [cpfLocked, setCpfLocked] = useState(false);
  const [birthDateLocked, setBirthDateLocked] = useState(false);
  const [username, setUsername] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [usernameRegistered, setUsernameRegistered] = useState(false);
  const usernameDebounceRef = useRef(null);

  const compactDigits = (value) => {
    if (!value) return '';
    return String(value)
      .replace(/\D/g, '')
      .slice(0, 11);
  };

  const formatCpf = (digits) => {
    if (!digits) return '';
    const onlyDigits = compactDigits(digits);
    const part1 = onlyDigits.slice(0, 3);
    const part2 = onlyDigits.slice(3, 6);
    const part3 = onlyDigits.slice(6, 9);
    const part4 = onlyDigits.slice(9, 11);
    let formatted = part1;
    if (part2) formatted += `.${part2}`;
    if (part3) formatted += `.${part3}`;
    if (part4) formatted += `-${part4}`;
    return formatted;
  };

  const normalizeBirthDate = (value) => {
    if (!value) return '';
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString().slice(0, 10);
    }
    if (typeof value !== 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
    }
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      return trimmed;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
      const [day, month, year] = trimmed.split('/');
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(trimmed);
    if (Number.isNaN(parsed.getTime())) {
      return '';
    }
    return parsed.toISOString().slice(0, 10);
  };

  useEffect(() => {
    if (!user?.id) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/account/profile/${user.id}`);
        const profile = response.data?.profile || {};

        const deriveNames = (value) => {
          if (typeof value !== 'string') return { first: '', last: '' };
          const trimmed = value.trim();
          if (!trimmed) return { first: '', last: '' };
          const parts = trimmed.split(' ').filter(Boolean);
          const first = parts.shift() || '';
          const last = parts.length ? parts.join(' ') : '';
          return { first, last };
        };

        const safe = (value) => (typeof value === 'string' ? value.trim() : '');

        const profileFirst = safe(profile.firstName ?? profile.firstname ?? profile.name);
        const profileLast = safe(profile.lastName ?? profile.lastname ?? '');
        const profileFull = safe(profile.fullName ?? profile.fullname ?? '');
        const profileCpf =
          compactDigits(
            profile.cpf ??
              profile.CPF ??
              profile.document ??
              profile.documentNumber ??
              profile.doc ??
              profile.taxId
          ) || '';
        const profileBirth =
          normalizeBirthDate(
            profile.birthDate ??
              profile.birthdate ??
              profile.dateOfBirth ??
              profile.birthday ??
              profile.DataNascimento ??
              profile.data_nascimento
          ) || '';
        const fallbackName = profileFull || safe(user.name) || '';

        let first = profileFirst;
        let last = profileLast;

        if (!first || !last) {
          const derived = deriveNames(fallbackName);
          if (!first) first = derived.first;
          if (!last) last = derived.last;
        }

        const hasSavedFirstName = !!profileFirst;
        const hasSavedLastName = !!profileLast;
        const bothNamesSaved = hasSavedFirstName && hasSavedLastName;

        setFirstName(first);
        setLastName(last);
        setNamesLocked(bothNamesSaved);
        setCpf(formatCpf(profileCpf));
        setBirthDate(profileBirth);
        setCpfLocked(Boolean(profileCpf));
        setBirthDateLocked(Boolean(profileBirth));

        if (profile.email && profile.validEmail !== undefined) {
          setEmailInfo({
            email: profile.email,
            verified: Number(profile.validEmail) === 1,
          });
        } else if (user.email) {
          setEmailInfo({
            email: user.email,
            verified: Number(user.validEmail) === 1,
          });
        }

        const existingUsername = safe(profile.username ?? profile.userName ?? user.username ?? user.userName ?? '');
        if (existingUsername) {
          setUsername(existingUsername);
          setUsernameRegistered(true);
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            err.message ||
            'Não foi possível carregar seus dados pessoais.'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id, user?.name, user?.email, user?.validEmail]);

  useEffect(() => {
    return () => {
      if (usernameDebounceRef.current) {
        clearTimeout(usernameDebounceRef.current);
      }
    };
  }, []);

  const handleSubmitNames = async (event) => {
    event.preventDefault();
    if (namesLocked || savingNames || !user?.id) return;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const combinedName = [trimmedFirst, trimmedLast].filter(Boolean).join(' ');

    setError('');
    setSuccess('');

    if (!trimmedFirst) return;

    try {
      setSavingNames(true);
      setError('');
      setSuccess('');

      await api.put('/account/profile', {
        userId: user.id,
        firstName: trimmedFirst,
        lastName: trimmedLast,
      });

      const updatedUser = { ...user, name: trimmedFirst, lastName: trimmedLast, fullName: combinedName };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      if (trimmedFirst && trimmedLast) {
        setNamesLocked(true);
      }

      setSuccess('Dados pessoais atualizados com sucesso.');
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível atualizar seus dados.';
      setError(message);
    } finally {
      setSavingNames(false);
    }
  };

  const handleSubmitPii = async (event) => {
    event.preventDefault();
    if ((cpfLocked && birthDateLocked) || savingPii || !user?.id) return;

    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    const digitsCpf = cpf.replace(/\D/g, '');
    const normalizedBirth = birthDate ? normalizeBirthDate(birthDate) : '';

    setError('');
    setSuccess('');

    if (digitsCpf.length !== 11) {
      setError('Informe um CPF válido.');
      return;
    }

    if (!normalizedBirth) {
      setError('Informe sua data de nascimento.');
      return;
    }

    const today = new Date();
    const birthDateObj = new Date(normalizedBirth);
    if (Number.isNaN(birthDateObj.getTime()) || birthDateObj > today) {
      setError('Informe uma data de nascimento válida.');
      return;
    }

    try {
      setSavingPii(true);
      let currentProfile = {};

      try {
        const currentResponse = await api.get(`/user/id/${user.id}`);
        currentProfile = currentResponse.data || {};
      } catch (fetchError) {
        console.warn('Não foi possível carregar dados atuais do usuário antes da atualização do PII.', fetchError);
      }

      const safeString = (value) => (typeof value === 'string' ? value : '');
      const existingFirstName =
        trimmedFirst || safeString(currentProfile.name) || safeString(currentProfile.firstName) || '';
      const existingLastName =
        trimmedLast ||
        safeString(currentProfile.lastname || currentProfile.lastName || currentProfile.surname || '');
      const resolvedFullName =
        [existingFirstName, existingLastName].filter(Boolean).join(' ') || safeString(currentProfile.fullName);

      const payload = {
        img: safeString(currentProfile.img),
        name: existingFirstName || resolvedFullName,
        lastname: existingLastName,
        profilename: safeString(currentProfile.profilename),
        nick: safeString(currentProfile.nick),
        email: safeString(currentProfile.email || user?.email),
        personalDescription: safeString(currentProfile.personalDescription),
        document: digitsCpf,
        documentNumber: digitsCpf,
        doc: digitsCpf,
        cpf: digitsCpf,
        taxId: digitsCpf,
        dateBirth: normalizedBirth,
        birthDate: normalizedBirth,
        birthdate: normalizedBirth,
        dateOfBirth: normalizedBirth,
        birthday: normalizedBirth,
        fullName: resolvedFullName,
      };

      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === null) {
          delete payload[key];
        }
      });

      await api.put(`/user/${user.id}`, payload);

      const updatedUser = {
        ...user,
        cpf: digitsCpf,
        document: digitsCpf,
        taxId: digitsCpf,
        dateBirth: normalizedBirth,
        birthDate: normalizedBirth,
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
      setCpf(formatCpf(digitsCpf));
      setBirthDate(normalizedBirth);
      setSuccess('Dados PII atualizados com sucesso.');
      setCpfLocked(true);
      setBirthDateLocked(true);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível atualizar suas informações para o nível 2.';
      setError(message);
    } finally {
      setSavingPii(false);
    }
  };

  const [emailInfo, setEmailInfo] = useState({
    email: user?.email || '',
    verified: Number(user?.validEmail) === 1,
  });

  const [verifyEmailStatus, setVerifyEmailStatus] = useState({
    loading: false,
    success: '',
    error: '',
  });

  const isEmailVerified = emailInfo.verified;

  const handleVerifyEmailRequest = async () => {
    if (verifyEmailStatus.loading || Number(user?.validEmail) === 1) {
      return;
    }

    const userUuid =
      user?.uuid || user?.Uuid || user?.userUuid || user?.UserUuid || user?.id || user?.ID || null;

    if (!userUuid) {
      setVerifyEmailStatus({
        loading: false,
        success: '',
        error: 'Não foi possível identificar o usuário para enviar a verificação.',
      });
      return;
    }

    try {
      setVerifyEmailStatus({ loading: true, success: '', error: '' });
      await api.post('/account/verify-email', { userUuid });
      setVerifyEmailStatus({
        loading: false,
        success:
          'Enviamos um email de confirmação. Verifique sua caixa de entrada e siga as instruções para validar o endereço.',
        error: '',
      });
    } catch (err) {
      setVerifyEmailStatus({
        loading: false,
        success: '',
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          err.message ||
          'Não foi possível solicitar a verificação. Tente novamente.',
      });
    }
  };

  const validateUsername = (value) => {
    if (!value || !value.trim()) return false;
    const trimmed = value.trim();
    return /^[a-zA-Z0-9_]+$/.test(trimmed) && trimmed.length >= 3 && trimmed.length <= 20;
  };

  const checkUsernameAvailability = async (value) => {
    if (!value || !value.trim()) {
      setUsernameAvailable(null);
      setCheckingUsername(false);
      return;
    }

    const trimmed = value.trim();
    if (!validateUsername(trimmed)) {
      setUsernameAvailable(false);
      setCheckingUsername(false);
      return;
    }

    try {
      setCheckingUsername(true);
      console.log('Verificando disponibilidade do username:', trimmed);
      const response = await api.post('/user/username/search', { username: trimmed });
      console.log('Resposta da API:', response.data);
      const available = response.data?.available ?? false;
      setUsernameAvailable(available);
    } catch (err) {
      console.error('Erro ao verificar username:', err);
      console.error('URL da requisição:', err.config?.url);
      console.error('Status:', err.response?.status);
      console.error('Dados do erro:', err.response?.data);
      
      if (err.response?.status === 404) {
        console.warn('Endpoint não encontrado. Verifique se o backend está rodando e se o endpoint existe.');
        setUsernameAvailable(null);
      } else {
        setUsernameAvailable(false);
      }
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    console.log('[USERNAME CHANGE] Valor digitado:', value);
    setUsername(value);
    setUsernameAvailable(null);

    if (usernameDebounceRef.current) {
      clearTimeout(usernameDebounceRef.current);
    }

    if (!value || !value.trim()) {
      console.log('[USERNAME CHANGE] Valor vazio, cancelando verificação');
      setCheckingUsername(false);
      return;
    }

    const trimmed = value.trim();
    const isValid = validateUsername(trimmed);
    console.log('[USERNAME CHANGE] Validando username:', { trimmed, isValid });

    setCheckingUsername(true);
    usernameDebounceRef.current = setTimeout(() => {
      console.log('[USERNAME CHANGE] Debounce expirado, chamando checkUsernameAvailability');
      checkUsernameAvailability(value);
    }, 500);
  };

  const registerUsername = async () => {
    const trimmed = username.trim();
    if (!trimmed || !validateUsername(trimmed) || !usernameAvailable || usernameRegistered) {
      return;
    }

    try {
      setSavingUsername(true);
      setError('');
      setSuccess('');

      await api.post('/user/username/new', { username: trimmed });

      setUsernameRegistered(true);
      setSuccess('Username cadastrado com sucesso.');
      
      const updatedUser = { ...user, username: trimmed };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Não foi possível cadastrar o username.';
      setError(message);
      setUsernameAvailable(false);
    } finally {
      setSavingUsername(false);
    }
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="account-section" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate('/account')}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="w-full">
            {loading ? (
              <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm lg:px-12">
                <h1 className="text-xl font-semibold text-slate-900 mb-6">Minha Conta</h1>
                <div className="text-sm text-gray-500">Carregando informações...</div>
              </div>
            ) : (
              <div className="space-y-6">
                {error && (
                  <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
                    {success}
                  </div>
                )}

                <div className="space-y-6 max-w-3xl mx-auto">
                  <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm lg:px-10">
                    <h2 className="text-xl font-semibold text-slate-900">Email</h2>
                    <div className="mt-6">
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-6 sm:px-6">
                        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                          <i className="bi-envelope text-slate-400 text-lg"></i>
                          <div className="flex-1 text-sm text-slate-500">{emailInfo.email || '-'}</div>
                          <div className="text-right">
                            {isEmailVerified ? (
                              <span className="text-xs font-semibold text-emerald-600">Verificado</span>
                            ) : (
                              <button
                                type="button"
                                className="text-xs font-semibold text-tBlue hover:text-tBlue2 transition-colors"
                                onClick={handleVerifyEmailRequest}
                                disabled={verifyEmailStatus.loading}
                              >
                                {verifyEmailStatus.loading ? 'Enviando...' : 'Não verificado'}
                              </button>
                            )}
                          </div>
                        </div>

                        {verifyEmailStatus.success ? (
                          <p className="mt-3 text-xs text-emerald-700 font-medium">
                            {verifyEmailStatus.success}
                          </p>
                        ) : null}
                        {verifyEmailStatus.error ? (
                          <p className="mt-3 text-xs text-red-600 font-medium">
                            {verifyEmailStatus.error}
                          </p>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm lg:px-10">
                    <h2 className="text-xl font-semibold text-slate-900">Username</h2>
                    <div className="mt-6">
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-6 sm:px-6">
                        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                          <i className="bi-person-badge text-slate-400 text-lg"></i>
                          <input
                            type="text"
                            value={username}
                            onChange={handleUsernameChange}
                            placeholder="Username"
                            disabled={usernameRegistered}
                            className={`flex-1 bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                              usernameRegistered ? 'cursor-not-allowed opacity-60' : ''
                            }`}
                          />
                          <div className="text-right min-w-[100px]">
                            {checkingUsername ? (
                              <span className="text-xs text-slate-400">Verificando...</span>
                            ) : usernameRegistered ? (
                              <span className="text-xs font-semibold text-emerald-600">Cadastrado</span>
                            ) : usernameAvailable === true ? (
                              <span className="text-xs font-semibold text-emerald-600">Disponível</span>
                            ) : usernameAvailable === false ? (
                              <span className="text-xs font-semibold text-red-600">Indisponível</span>
                            ) : username.trim() && !validateUsername(username.trim()) ? (
                              <span className="text-xs font-semibold text-red-600">Inválido</span>
                            ) : null}
                          </div>
                        </div>
                        {username.trim() && !validateUsername(username.trim()) && (
                          <p className="mt-3 text-xs text-red-600">
                            Username deve conter apenas letras, números e underscore (3-20 caracteres).
                          </p>
                        )}
                        {usernameRegistered && (
                          <p className="mt-3 text-xs text-slate-500">
                            Username já cadastrado. Entre em contato com o suporte se precisar alterar.
                          </p>
                        )}
                      </div>
                      {!usernameRegistered && (
                        <button
                          type="button"
                          onClick={registerUsername}
                          disabled={
                            savingUsername ||
                            !username.trim() ||
                            !validateUsername(username.trim()) ||
                            !usernameAvailable ||
                            checkingUsername
                          }
                          className={`mt-6 inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                            savingUsername ||
                            !username.trim() ||
                            !validateUsername(username.trim()) ||
                            !usernameAvailable ||
                            checkingUsername
                              ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                              : 'bg-tBlue text-white hover:bg-tBlue2'
                          }`}
                        >
                          {savingUsername ? 'Cadastrando...' : 'Cadastrar'}
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm lg:px-10">
                    <h2 className="text-xl font-semibold text-slate-900">Nome Completo</h2>
                    <form className="mt-6 flex flex-col" onSubmit={handleSubmitNames}>
                      <div className="rounded-3xl border border-slate-200 bg-slate-50 px-4 py-6 sm:px-6">                        
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                            <i className="bi-person text-slate-400 text-lg"></i>
                            <input
                              type="text"
                              value={firstName}
                              onChange={(e) => setFirstName(e.target.value)}
                              placeholder="Nome"
                              disabled={namesLocked}
                              className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                                namesLocked ? 'cursor-not-allowed opacity-60' : ''
                              }`}
                            />
                          </div>
                          <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                            <i className="bi-person-lines-fill text-slate-300 text-lg"></i>
                            <input
                              type="text"
                              value={lastName}
                              onChange={(e) => setLastName(e.target.value)}
                              placeholder="Sobrenome"
                              disabled={namesLocked}
                              className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                                namesLocked ? 'cursor-not-allowed opacity-60' : ''
                              }`}
                            />
                          </div>
                          {namesLocked && (
                            <p className="text-xs text-slate-500">
                              <span>Nome e sobrenome não podem ser alterados após serem salvos.</span>
                              <br />
                              <span>Entre em contato com o suporte se precisar fazer alterações.</span>
                            </p>
                          )}
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={namesLocked || !firstName.trim() || savingNames}
                        className={`mt-6 inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                          namesLocked || !firstName.trim() || savingNames
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-tBlue text-white hover:bg-tBlue2'
                        }`}
                      >
                        {savingNames ? 'Atualizando...' : 'Atualizar'}
                      </button>
                    </form>
                  </div>

                  <div className="rounded-3xl border border-slate-200 bg-white px-6 py-8 shadow-sm lg:px-10">
                    <h2 className="text-xl font-semibold text-slate-900">Dados Pessoais</h2>
                    <form className="mt-6 flex flex-col" onSubmit={handleSubmitPii}>
                      <div className="flex flex-col gap-4">
                        <div
                          className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 ${
                            cpfLocked ? 'opacity-60' : ''
                          }`}
                        >
                          <i className="bi-card-text text-slate-400 text-lg"></i>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={cpf}
                            onChange={(e) => setCpf(formatCpf(e.target.value))}
                            placeholder="CPF"
                            disabled={cpfLocked}
                            className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                              cpfLocked ? 'cursor-not-allowed' : ''
                            }`}
                          />
                        </div>
                        <div
                          className={`flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 ${
                            birthDateLocked ? 'opacity-60' : ''
                          }`}
                        >
                          <i className="bi-calendar3 text-slate-400 text-lg"></i>
                          <input
                            type="date"
                            value={birthDate}
                            max={new Date().toISOString().slice(0, 10)}
                            onChange={(e) => setBirthDate(normalizeBirthDate(e.target.value))}
                            placeholder="Data de nascimento"
                            disabled={birthDateLocked}
                            className={`w-full bg-transparent text-sm outline-none placeholder:text-slate-400 ${
                              birthDateLocked ? 'cursor-not-allowed' : ''
                            }`}
                          />
                        </div>
                        {(cpfLocked || birthDateLocked) && (
                          <p className="text-xs text-slate-500">
                            Para atualizar CPF ou data de nascimento, fale com o suporte.
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={
                          savingPii ||
                          cpf.replace(/\D/g, '').length !== 11 ||
                          !birthDate ||
                          (cpfLocked && birthDateLocked)
                        }
                        className={`mt-6 inline-flex items-center justify-center rounded-full px-6 py-2 text-sm font-semibold transition-colors ${
                          savingPii ||
                          cpf.replace(/\D/g, '').length !== 11 ||
                          !birthDate ||
                          (cpfLocked && birthDateLocked)
                            ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            : 'bg-tBlue text-white hover:bg-tBlue2'
                        }`}
                      >
                        {cpfLocked && birthDateLocked
                          ? 'Dados confirmados'
                          : savingPii
                            ? 'Atualizando...'
                            : 'Atualizar'}
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </div>
  );
};

export default PersonalData;

